import { useState } from "react";
import type { Session } from "../lib/session";
import { useKeyStore } from "../store/keyStore";
import { useSessionStore } from "../store/sessionStore";
import { useChannelKeyStore } from "../store/channelKeyStore";
import { encryptKeys, type PlainKeys } from "../lib/channel-crypto";

interface Props {
  session: Session;
  onClose: () => void;
}

export function AttachKeysModal({ session, onClose }: Props) {
  const { babyai, anthropic } = useKeyStore();
  const { setEncryptedKeys } = useSessionStore();
  const channelKeyStore = useChannelKeyStore();

  const [pass1, setPass1] = useState("");
  const [pass2, setPass2] = useState("");
  const [includeBabyAI, setIncludeBabyAI] = useState(!!babyai);
  const [includeAnthropic, setIncludeAnthropic] = useState(!!anthropic);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasAny = !!(babyai || anthropic);
  const alreadyAttached = !!session.encrypted_keys;

  async function attach() {
    setError(null);
    if (pass1.length < 8) {
      setError("Use a passphrase of at least 8 characters.");
      return;
    }
    if (pass1 !== pass2) {
      setError("Passphrases don't match.");
      return;
    }
    const toEncrypt: PlainKeys = {};
    if (includeBabyAI && babyai) toEncrypt.babyai = babyai;
    if (includeAnthropic && anthropic) toEncrypt.anthropic = anthropic;
    if (Object.keys(toEncrypt).length === 0) {
      setError("Select at least one key to attach.");
      return;
    }

    setBusy(true);
    try {
      const blob = await encryptKeys(toEncrypt, pass1);
      setEncryptedKeys(session.id, blob);
      // Owner already has the plaintext; pre-populate the unlock cache
      // so they don't have to re-enter their own passphrase to chat.
      channelKeyStore.set(session.id, toEncrypt);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function remove() {
    if (!confirm("Remove the encrypted keys from this channel?")) return;
    setEncryptedKeys(session.id, null);
    channelKeyStore.clear(session.id);
    onClose();
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Attach keys to "{session.title}"</h2>
        <div className="subtitle">
          Encrypt your local keys with a passphrase and ship them with the channel.
          Anyone you share the channel with enters this passphrase to use the keys —
          they never need their own.
        </div>

        {!hasAny && (
          <div className="share-warn">
            You don't have any personal keys set yet. Add a BabyAI or Anthropic key
            in <strong>Keys</strong> first, then come back here.
          </div>
        )}

        {hasAny && (
          <>
            <div className="field">
              <label>Include keys</label>
              {babyai && (
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={includeBabyAI}
                    onChange={(e) => setIncludeBabyAI(e.target.checked)}
                  />
                  BabyAI token
                </label>
              )}
              {anthropic && (
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    checked={includeAnthropic}
                    onChange={(e) => setIncludeAnthropic(e.target.checked)}
                  />
                  Anthropic key
                </label>
              )}
            </div>

            <div className="field">
              <label>Passphrase (8+ characters)</label>
              <input
                type="password"
                value={pass1}
                onChange={(e) => setPass1(e.target.value)}
                autoComplete="new-password"
                autoFocus
              />
            </div>

            <div className="field">
              <label>Confirm passphrase</label>
              <input
                type="password"
                value={pass2}
                onChange={(e) => setPass2(e.target.value)}
                autoComplete="new-password"
                onKeyDown={(e) => { if (e.key === "Enter") attach(); }}
              />
            </div>

            {error && <div className="share-warn">{error}</div>}

            <div className="subtitle" style={{ marginTop: 8 }}>
              Share the passphrase out-of-band — DM, voice, paper. Don't put it
              in the same place as the channel link.
            </div>
          </>
        )}

        <div className="actions">
          {alreadyAttached && (
            <button className="btn" style={{ marginRight: "auto", color: "var(--danger)" }} onClick={remove}>
              Remove
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={attach} disabled={!hasAny || busy}>
            {busy ? "Encrypting…" : alreadyAttached ? "Replace" : "Attach"}
          </button>
        </div>
      </div>
    </div>
  );
}

